import React, { useState, useEffect } from 'react';
import styles from './customFileUploader.module.scss';
import { mdiPlus, mdiTrashCanOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { IconButton } from "@mui/material";
import { CustomFileUploaderProps } from '../../interfaces/components';
import { useToast } from "../../helpers/hooks";

const CustomFileUploader = ({
    values,
    onChange = () => {},
    className,
    placeholderImage,
    disabled,
    loadingStates = true,
    enumeration = true,
    draggable = true,
    scroll = null,
    read = false,
    showInvalids = false,
    bigPictures = false,
    selectedPicture,
    setSelectedPicture,
    onDeleteItem,
    onAddItem,
    onMoveItem,
    reloadedName,
    onPressReload,
    showDefaultContent = false,
    multiple = false,
    loading = false, 
    deleting = false,
    hideDeleteButton = false,
}: CustomFileUploaderProps) => {
    const [localImage, setLocalImage] = useState<string | null>(null);
    const [localImages, setLocalImages] = useState<(string | null)[]>([]); 
    
    const toast = useToast();
    useEffect(() => {
        const imageUrl = values?.[0]?.previewURL || values?.[0]?.url;
        if (imageUrl) {
            setLocalImage(imageUrl);
        }
    }, [values]);

    const handleFileChange= (event: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) {
            toast("Has alcanzado el límite máximo de imágenes permitidas");
            return;
        }
    
        const files = event.target.files;
        if (!files || files.length === 0) return;
    
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
        const newImages: any[] = [];
    
        Array.from(files).forEach((file, index) => {
            if (!allowedTypes.includes(file.type)) {
                toast("Formato de archivo no válido. Solo se permiten imágenes JPEG, PNG o WEBP.");
                return;
            }
    
            if (file.size === 0) {
                toast("Archivo vacío no permitido.");
                return;
            }
            
    
            const previewURL = URL.createObjectURL(file);
            const id = Math.floor((index + Math.random()) * 0x10000).toString(16);
    
            newImages.push({
                file: file,
                id: Math.floor((index + Math.random()) * 0x10000).toString(16),
                previewURL: URL.createObjectURL(file),
                name: `${Math.floor((index + Math.random()) * 0x10000).toString(16)}.jpg`,
                upload: loadingStates ? "loading" : "success",
                toDelete: false
            });
        });
    
        if (newImages.length > 0) {
            const updatedValues = multiple
                ? [...(values || []), ...newImages]
                : [newImages[0]];
    
            setLocalImage(newImages[0]?.previewURL); 
            onChange(updatedValues);
        }
    
        event.target.value = ""; 
    };
    
    
    const handleRemoveImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        
        setLocalImage(null);
        
        const currentValue = values && values.length > 0 ? values[0] : null;
        
        if (currentValue) {
            if (currentValue.id) {
                const updatedValue = {
                    ...currentValue,
                    toDelete: true,
                    deleted: true,
                    previewURL: "",
                };
                
                if (onDeleteItem) {
                    
                    onDeleteItem(currentValue);
                } else {
                   
                    onChange([updatedValue]);
                }
            } else {
                
                onChange([]);
            }
        } else {
        
            onChange([]);
        }
    };
    
    const displayImage = localImage;

    return (
        <div className={`${styles.customFileUploader} ${className || ''}`}>
            <div className={styles.uploader}>
                {!displayImage && (
                    <input
                        type="file"
                        onChange={handleFileChange}
                        accept="image/*"
                        disabled={disabled}
                        className={styles.input}
                        multiple={multiple}
                    />
                )}
                {!displayImage ? (
                    <div className={styles.placeholder}>
                        {placeholderImage ? (
                            <div>
                                <img src={placeholderImage} alt="Upload placeholder" title="Upload placeholder" />
                                <p>Selecciona para cargar</p>
                            </div>
                        ) : (
                            <Icon path={mdiPlus} size={2} />
                        )}
                    </div>
                ) : (
                    <div className={`${styles.preview} ${loading ? styles.loading : ''}`}>
                    <img src={displayImage} alt="Vista previa" title="Vista previa" />
                    
                    {loading ? (
                      <div className={styles.loaderOverlay}>
                        <span className={styles.loader}></span>
                      </div>
                    ) : (
                      !hideDeleteButton && (
                        <IconButton 
                          aria-label='Delete' 
                          className={styles.delete} 
                          onClick={handleRemoveImage}
                          disabled={deleting}
                        >
                          <Icon path={mdiTrashCanOutline} title="Eliminar" className={styles.icon} />
                        </IconButton>
                      )
                    )}
                  </div>
                )}
            </div>
        </div>
    );
};

export default CustomFileUploader;
